
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface CTraderAccount {
  id: string;
  csp_account_name: string;
  csp_account_email: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  status: string;
  total_api_connections: number;
  total_trading_accounts: number;
  broker?: string;
  prop_firm?: string;
}

export function useCTraderAccounts() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['ctraderAccounts', user?.id],
    queryFn: async (): Promise<CTraderAccount[]> => {
      if (!user?.id) return [];
      
      // Get basic cTrader account data
      const { data, error } = await supabase
        .from('csp_accounts')
        .select('*')
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      if (!data || data.length === 0) return [];
      
      // Enhance accounts with API connection and trading account counts
      const enhancedAccounts = await Promise.all(data.map(async (account) => {
        // Count API connections for this cTrader account
        const { count: apiConnectionCount, error: apiError } = await supabase
          .from('api_connections')
          .select('id', { count: 'exact', head: true })
          .eq('csp_account_id', account.id);
          
        if (apiError) {
          console.error('Error fetching API connections count:', apiError);
        }
        
        // Get API connection IDs for this cTrader account
        const { data: apiConnections, error: apiConnectionsError } = await supabase
          .from('api_connections')
          .select('id')
          .eq('csp_account_id', account.id);
          
        if (apiConnectionsError) {
          console.error('Error fetching API connections:', apiConnectionsError);
        }
        
        // Count trading accounts for all API connections of this cTrader account
        let tradingAccountCount = 0;
        if (apiConnections && apiConnections.length > 0) {
          const apiConnectionIds = apiConnections.map(conn => conn.id);
          
          const { count, error: tradingError } = await supabase
            .from('trading_accounts')
            .select('*', { count: 'exact', head: true })
            .in('api_connection_id', apiConnectionIds);
            
          if (tradingError) {
            console.error('Error fetching trading accounts count:', tradingError);
          } else {
            tradingAccountCount = count || 0;
          }
        }
        
        return {
          ...account,
          total_api_connections: apiConnectionCount || 0,
          total_trading_accounts: tradingAccountCount
        };
      }));
      
      return enhancedAccounts;
    },
    enabled: !!user?.id,
  });
}
